import React, { useEffect, useState } from 'react';
import { scroller, Link } from "react-scroll";
import { OctagonAlert } from 'lucide-react';
import { getMainTranslation } from '../../hooks/useLanguage';
import { useSelector } from 'react-redux';
import collapseIcon from "../../assets/collapse-icon.png";
import '../Sidebar/Sidebar.css';

export const FieldOverviewPanel = ({ 
  extractedData, 
  activeTab, 
  activeSubTab, 
  setActiveTab, 
  setActiveSubTab, 
  isSpanish,
  validationErrors,
  taricJsonErrors,
  ValidationErrors,
  TaricJsonErrors,
  location,
  splitView,
  isFieldOverviewCollapsed = false,
  setIsFieldOverviewCollapsed = () => {}
}) => {
  const [requiredFilter, setRequiredFilter] = useState('all'); // 'all', 'required', 'nonRequired'
  // Function to get field display name based on field name and language
  const getFieldDisplayName = (fieldName) => {
    // Extract the actual field name from paths like "partidas[0].regimen_comunitario_nacional" or "partidas[0].key"
    let actualFieldName = fieldName;
    
    // Handle documents_ and packages_ prefixes
    if (fieldName.startsWith('documents_')) {
      actualFieldName = fieldName.replace('documents_', '');
    } else if (fieldName.startsWith('packages_')) {
      if (fieldName.startsWith('packages_marcas_marca')) {
        actualFieldName = 'marca';
      } else {
        actualFieldName = fieldName.replace('packages_', '');
      }
    } else if (fieldName.includes('.')) {
      actualFieldName = fieldName.split('.').pop();
    } else if (fieldName.includes('[') && fieldName.includes(']')) {
      // Handle cases like "partidas[0]" - extract just the base name
      actualFieldName = fieldName.split('[')[0];
    }
    
    // Handle confidence fields - extract base field name and add "Confidence" label
    let isConfidenceField = false;
    let baseFieldNameForConfidence = actualFieldName;
    if (actualFieldName.endsWith('_confidence')) {
      isConfidenceField = true;
      baseFieldNameForConfidence = actualFieldName.replace('_confidence', '');
      actualFieldName = baseFieldNameForConfidence; // Use base name for lookup
    }
    
    const fieldMappings = {
      // Declaration fields
      expediente: !isSpanish ? "Declaration" : "Expediente",
      tipo: !isSpanish ? "Type" : "Tipo",
      fecha_registro: !isSpanish ? "Registration Date" : "Fecha Registro",
      fecha_creacion: !isSpanish ? "Creation Date" : "Fecha Creación",
      cliente_id: !isSpanish ? "Client ID" : "Cliente ID",
      agente_id: !isSpanish ? "Agent ID" : "Agente ID",
      remitente_id: !isSpanish ? "Sender ID" : "Remitente ID",
      declaracion_1: !isSpanish ? "Declaration 1" : "Declaración 1",
      declaracion_2: !isSpanish ? "Declaration 2" : "Declaración 2",
      declaracion_3: !isSpanish ? "Declaration 3" : "Declaración 3",
      aduana_entrada: !isSpanish ? "Entry Customs" : "Aduana Entrada",
      aduana_salida: !isSpanish ? "Exit Customs" : "Aduana Salida",
      aduana_despacho: !isSpanish ? "Clearance Customs" : "Aduana Despacho",
      pais_destino: !isSpanish ? "Destination Country" : "País Destino",
      pais_exportacion: !isSpanish ? "Export Country" : "País Exportación",
      pais_transporte_frontera: !isSpanish ? "Border Transport Country" : "País Transporte Frontera",
      modo_transporte_frontera: !isSpanish ? "Border Transport Mode" : "Modo Transporte Frontera",
      modo_transporte_interior: !isSpanish ? "Internal Transport Mode" : "Modo Transporte Interior",
      identidad_transporte_frontera: !isSpanish ? "Border Transport Identity" : "Identidad Transporte Frontera",
      factura: !isSpanish ? "Invoice" : "Factura",
      fecha_factura: !isSpanish ? "Invoice Date" : "Fecha Factura",
      condiciones_entrega: !isSpanish ? "Delivery Terms" : "Condiciones Entrega",
      naturaleza_transaccion: !isSpanish ? "Transaction Nature" : "Naturaleza Transacción",
      observaciones: !isSpanish ? "Observations" : "Observaciones",
      tiene_contenedores: !isSpanish ? "Has Containers" : "Tiene Contenedores",
      total_bultos: !isSpanish ? "Total Packages" : "Total Bultos",
      estado_edi: !isSpanish ? "EDI Status" : "Estado EDI",
      estado_edi_canarias: !isSpanish ? "Canary Islands EDI Status" : "Estado EDI Canarias",
      estado_pdc: !isSpanish ? "PDC Status" : "Estado PDC",
      autorizacion_por_operacion: !isSpanish ? "Authorization per Operation" : "Autorización por Operación",
      es_simplificado: !isSpanish ? "Simplified" : "Simplificado",
      es_recapitulativo: !isSpanish ? "Recapitulative" : "Recapitulativo",
      completo: !isSpanish ? "Complete" : "Completo",
      es_plantilla: !isSpanish ? "Is Template" : "¿Es Plantilla?",
      e_mail: !isSpanish ? "Contact Email" : "Correo Electrónico de Contacto",
      contacto_telefono: !isSpanish ? "Contact Phone" : "Teléfono de Contacto",
      pais_ubicacion: !isSpanish ? "Location Country" : "País Ubicación",
      tipo_ubicacion: !isSpanish ? "Location Type" : "Tipo Ubicación",
      localizacion: !isSpanish ? "Location Code" : "Código de Localización",
      divisa: !isSpanish ? "Currency Code" : "Código Divisa",
      cambio_divisa: !isSpanish ? "Exchange Rate" : "Cambio Divisa",
      divisa_iso: !isSpanish ? "Currency ISO" : "Divisa ISO",
      pais: !isSpanish ? "Country" : "País",
      
      // Item fields
      descripcion_mercancia: !isSpanish ? "Goods Description" : "Descripción de Mercancía",
      codigo_nc: !isSpanish ? "NC Code" : "Código NC",
      masa_bruta: !isSpanish ? "Gross Weight" : "Masa Bruta",
      masa_neta: !isSpanish ? "Net Weight" : "Masa Neta",
      pais_origen: !isSpanish ? "Country of Origin" : "País de Origen",
      cantidad_euros: !isSpanish ? "Euro Amount" : "Cantidad en Euros",
      cantidad_divisas: !isSpanish ? "Currency Amount" : "Cantidad en Divisas",
      precio: !isSpanish ? "Price" : "Precio",
      provincia_origen: !isSpanish ? "Province of Origin" : "Provincia de Origen",
      regimen_solicitado_precedente: !isSpanish ? "Previous Regime" : "Régimen Solicitado",
      regimen_comunitario_nacional: !isSpanish ? "Community/National Regime" : "Régimen Comunitario Nacional",
      valor_estadistico: !isSpanish ? "Statistical Value" : "Valor Estadístico",
      adiciones: !isSpanish ? "Additions" : "Adiciones",
      deducciones: !isSpanish ? "Deductions" : "Deducciones",
      observaciones_arancelarias: !isSpanish ? "Customs Remarks" : "Observaciones Arancelarias",
      observaciones_arancelarias_2: !isSpanish ? "Customs Remarks 2" : "Observaciones Arancelarias 2",
      deducciones_base_iva: !isSpanish ? "VAT Deduction Base" : "Deducciones Base IVA",
      
      // Document fields
      codigo_documento: !isSpanish ? "Document Code" : "Código de Documento",
      tipo: !isSpanish ? "Type" : "Tipo",
      numero: !isSpanish ? "Number" : "Número",
      fecha: !isSpanish ? "Date" : "Fecha",
      
      // Package fields
      cantidad: !isSpanish ? "Quantity" : "Cantidad",
      cantidad_restante: !isSpanish ? "Remaining Quantity" : "Cantidad Restante",
      piezas: !isSpanish ? "Pieces" : "Piezas",
      es_interno_o_externo: !isSpanish ? "Internal/External" : "Interno/Externo",
      marca: !isSpanish ? "Mark" : "Marca",
    };
    
    // Get the base label
    let baseLabel;
    if (fieldMappings[actualFieldName]) {
      baseLabel = fieldMappings[actualFieldName];
    } else {
      // Otherwise, format the field name nicely
      // Convert underscores to spaces and capitalize each word
      baseLabel = actualFieldName
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    
    // If it's a confidence field, append "(Confidence)" or "(Confianza)"
    if (isConfidenceField) {
      return `${baseLabel} ${!isSpanish ? '(Confidence)' : '(Confianza)'}`;
    }
    
    return baseLabel;
  };

  // Function to generate field ID based on field path
  const getFieldId = (field, declarationIndex, itemIndex, docIndex, embajaIndex, marcaIndex) => {
    // Check if it's a document or package field (has prefix)
    if (field.startsWith('documents_')) {
      // Document field: documents_fieldName_declarationIndex+1_itemIndex_docIndex
      const fieldName = field.replace('documents_', '');
      return `documents_${fieldName}_${declarationIndex + 1}_${itemIndex}_${docIndex}`;
    } else if (field.startsWith('packages_')) {
      // Package field: packages_fieldName_declarationIndex+1_itemIndex_embajaIndex
      let fieldName = field.replace('packages_', '');
      if (fieldName.startsWith('marcas_marca')) {
        // Package marca field: packages_marcas_marca_declarationIndex+1_itemIndex_embajaIndex_marcaIndex
        return `packages_marcas_marca_${declarationIndex + 1}_${itemIndex}_${embajaIndex}_${marcaIndex}`;
      }
      return `packages_${fieldName}_${declarationIndex + 1}_${itemIndex}_${embajaIndex}`;
    }
    
    // Extract the actual field name for regular fields
    let actualFieldName = field;
    if (field.includes('.')) {
      actualFieldName = field.split('.').pop();
    } else if (field.includes('[') && field.includes(']')) {
      actualFieldName = field.split('[')[0];
    }
    
    // Generate ID based on whether it's an item field or declaration field
    if (itemIndex !== undefined) {
      // Item field: fieldName_declarationIndex+1_itemIndex
      return `${actualFieldName}_${declarationIndex + 1}_${itemIndex}`;
    } else {
      // Declaration field: fieldName_declarationIndex+1
      return `${actualFieldName}_${declarationIndex + 1}`;
    }
  };

  // Normalize field ID from validationErrors to match FieldOverviewPanel format
  // ValidationErrors uses declarationIndex (0-based) for documents/packages, but +1 for others
  // FieldOverviewPanel uses declarationIndex + 1 for all fields
  const normalizeValidationErrorFieldId = (fieldId) => {
    if (!fieldId) return fieldId;
    
    // Handle document fields: documents_fieldName_declarationIndex_itemIndex_docIndex
    // Convert to: documents_fieldName_declarationIndex+1_itemIndex_docIndex
    const docMatch = fieldId.match(/^documents_([^_]+)_(\d+)_(\d+)_(\d+)$/);
    if (docMatch) {
      const [, fieldName, declIdx, itemIdx, docIdx] = docMatch;
      return `documents_${fieldName}_${parseInt(declIdx) + 1}_${itemIdx}_${docIdx}`;
    }
    
    // Handle package fields: packages_fieldName_declarationIndex_itemIndex_embajaIndex
    // Convert to: packages_fieldName_declarationIndex+1_itemIndex_embajaIndex
    const pkgMatch = fieldId.match(/^packages_([^_]+)_(\d+)_(\d+)_(\d+)$/);
    if (pkgMatch) {
      const [, fieldName, declIdx, itemIdx, embajaIdx] = pkgMatch;
      return `packages_${fieldName}_${parseInt(declIdx) + 1}_${itemIdx}_${embajaIdx}`;
    }
    
    // Handle package marca fields: packages_marcas_marca_declarationIndex_itemIndex_embajaIndex_marcaIndex
    // Convert to: packages_marcas_marca_declarationIndex+1_itemIndex_embajaIndex_marcaIndex
    const marcaMatch = fieldId.match(/^packages_marcas_marca_(\d+)_(\d+)_(\d+)_(\d+)$/);
    if (marcaMatch) {
      const [, declIdx, itemIdx, embajaIdx, marcaIdx] = marcaMatch;
      return `packages_marcas_marca_${parseInt(declIdx) + 1}_${itemIdx}_${embajaIdx}_${marcaIdx}`;
    }
    
    // For other fields (declaration and item fields), they already use +1 format
    return fieldId;
  };

  // Generate all required field IDs based on data structure (not just missing ones)
  // Must match the format used by getFieldId() which uses declarationIndex + 1 for all fields
  const getAllRequiredFieldIds = () => {
    const fieldIds = new Set();
    
    if (!extractedData) {
      return fieldIds;
    }
    
    const declarations = Array.isArray(extractedData) ? extractedData : [extractedData];
    
    declarations.forEach((declaration, declarationIndex) => {
      // Declaration required fields (using declarationIndex + 1)
      fieldIds.add(`expediente_${declarationIndex + 1}`);
      fieldIds.add(`tipo_${declarationIndex + 1}`);
      fieldIds.add(`clave_declarante_${declarationIndex + 1}`); // Clave Declarante is CRITICAL
      fieldIds.add(`contacto_telefono_${declarationIndex + 1}`); // Phone number is CRITICAL
      fieldIds.add(`flag_datos_seguridad_${declarationIndex + 1}`); // Security Data Flag is CRITICAL
      
      // Item required fields
      declaration.partidas?.forEach((item, itemIndex) => {
        fieldIds.add(`descripcion_mercancia_${declarationIndex + 1}_${itemIndex}`);
        fieldIds.add(`codigo_nc_${declarationIndex + 1}_${itemIndex}`);
        fieldIds.add(`masa_bruta_${declarationIndex + 1}_${itemIndex}`);
        fieldIds.add(`masa_neta_${declarationIndex + 1}_${itemIndex}`);
        fieldIds.add(`pais_origen_${declarationIndex + 1}_${itemIndex}`);
        
        // Package required fields (using declarationIndex + 1 to match getFieldId format)
        item.embalajes?.forEach((embalaje, embalajeIndex) => {
          fieldIds.add(`packages_tipo_${declarationIndex + 1}_${itemIndex}_${embalajeIndex}`);
          fieldIds.add(`packages_es_interno_o_externo_${declarationIndex + 1}_${itemIndex}_${embalajeIndex}`);
          fieldIds.add(`packages_cantidad_${declarationIndex + 1}_${itemIndex}_${embalajeIndex}`);
          
          embalaje.marcas?.forEach((marca, marcaIndex) => {
            fieldIds.add(`packages_marcas_marca_${declarationIndex + 1}_${itemIndex}_${embalajeIndex}_${marcaIndex}`);
          });
        });
        
        // Document required fields (using declarationIndex + 1 to match getFieldId format)
        item.documentos?.forEach((documento, docIndex) => {
          fieldIds.add(`documents_codigo_documento_${declarationIndex + 1}_${itemIndex}_${docIndex}`);
          fieldIds.add(`documents_tipo_${declarationIndex + 1}_${itemIndex}_${docIndex}`);
        });
      });
    });
    
    return fieldIds;
  };

  // Extract all field IDs from validationErrors and normalize them
  const getValidationErrorFieldIds = () => {
    // Use getAllRequiredFieldIds to get all required fields, not just missing ones
    return getAllRequiredFieldIds();
  };

  // Check if a field has a corresponding input element
  const hasInputElement = (fieldId) => {
    return document.getElementById(fieldId) !== null;
  };

  // Check if a field is marked as required in validationErrors
  const isFieldInValidationErrors = (fieldId, validationErrorFieldIds) => {
    return validationErrorFieldIds.has(fieldId);
  };

  // Get field label from validationErrors to match ValidationErrors.jsx display
  const getFieldLabelFromValidationErrors = (fieldId) => {
    if (!validationErrors || !Array.isArray(validationErrors)) {
      return null;
    }

    // Normalize fieldId to match validationErrors format (0-based for docs/packages)
    // Try multiple formats to find the matching error
    const searchFieldIds = [fieldId]; // Start with original
    
    // Handle document fields: documents_fieldName_declarationIndex+1_itemIndex_docIndex
    // Convert to: documents_fieldName_declarationIndex_itemIndex_docIndex (0-based)
    const docMatch = fieldId.match(/^documents_(.+)_(\d+)_(\d+)_(\d+)$/);
    if (docMatch) {
      const [, fieldName, declIdx, itemIdx, docIdx] = docMatch;
      const declIdx0Based = parseInt(declIdx) - 1;
      searchFieldIds.push(`documents_${fieldName}_${declIdx0Based}_${itemIdx}_${docIdx}`);
    }
    
    // Handle package fields: packages_fieldName_declarationIndex+1_itemIndex_embajaIndex
    // Convert to: packages_fieldName_declarationIndex_itemIndex_embajaIndex (0-based)
    const pkgMatch = fieldId.match(/^packages_(.+)_(\d+)_(\d+)_(\d+)$/);
    if (pkgMatch && !fieldId.startsWith('packages_marcas_marca')) {
      const [, fieldName, declIdx, itemIdx, embajaIdx] = pkgMatch;
      const declIdx0Based = parseInt(declIdx) - 1;
      searchFieldIds.push(`packages_${fieldName}_${declIdx0Based}_${itemIdx}_${embajaIdx}`);
    }
    
    // Handle package marca fields: packages_marcas_marca_declarationIndex+1_itemIndex_embajaIndex_marcaIndex
    // Convert to: packages_marcas_marca_declarationIndex_itemIndex_embajaIndex_marcaIndex (0-based)
    const marcaMatch = fieldId.match(/^packages_marcas_marca_(\d+)_(\d+)_(\d+)_(\d+)$/);
    if (marcaMatch) {
      const [, declIdx, itemIdx, embajaIdx, marcaIdx] = marcaMatch;
      const declIdx0Based = parseInt(declIdx) - 1;
      searchFieldIds.push(`packages_marcas_marca_${declIdx0Based}_${itemIdx}_${embajaIdx}_${marcaIdx}`);
    }
    
    // Handle item fields: fieldName_declarationIndex+1_itemIndex
    // ValidationErrors uses the same format (declarationIndex + 1), so no conversion needed
    // But try both formats just in case
    const itemMatch = fieldId.match(/^(.+)_(\d+)_(\d+)$/);
    if (itemMatch && !fieldId.startsWith('documents_') && !fieldId.startsWith('packages_')) {
      const [, fieldName, declIdx, itemIdx] = itemMatch;
      // Try with 0-based declarationIndex
      const declIdx0Based = parseInt(declIdx) - 1;
      searchFieldIds.push(`${fieldName}_${declIdx0Based}_${itemIdx}`);
    }
    
    // Handle declaration fields: fieldName_declarationIndex+1
    // ValidationErrors uses the same format, so no conversion needed
    // But try both formats just in case
    const declMatch = fieldId.match(/^(.+)_(\d+)$/);
    if (declMatch && !fieldId.startsWith('documents_') && !fieldId.startsWith('packages_') && !itemMatch) {
      const [, fieldName, declIdx] = declMatch;
      // Try with 0-based declarationIndex
      const declIdx0Based = parseInt(declIdx) - 1;
      searchFieldIds.push(`${fieldName}_${declIdx0Based}`);
    }

    // Search through validationErrors for matching field (try all searchFieldIds)
    for (const declaration of validationErrors) {
      // Check declaration errors
      if (declaration.declarationErrors) {
        for (const searchId of searchFieldIds) {
          const found = declaration.declarationErrors.find(err => err.field === searchId);
          if (found) return found.error;
        }
      }
      
      // Check item errors
      if (declaration.itemErrors) {
        for (const item of declaration.itemErrors) {
          if (item.errors) {
            for (const searchId of searchFieldIds) {
              const found = item.errors.find(err => err.field === searchId);
              if (found) return found.error;
            }
          }
        }
      }
      
      // Check document errors
      if (declaration.documentErrors) {
        for (const doc of declaration.documentErrors) {
          if (doc.errors) {
            for (const searchId of searchFieldIds) {
              const found = doc.errors.find(err => err.field === searchId);
              if (found) return found.error;
            }
          }
        }
      }
      
      // Check package errors
      if (declaration.packageErrors) {
        for (const pkg of declaration.packageErrors) {
          if (pkg.errors) {
            for (const searchId of searchFieldIds) {
              const found = pkg.errors.find(err => err.field === searchId);
              if (found) return found.error;
            }
          }
        }
      }
    }
    
    return null;
  };

  // Handle field click to scroll to input
  const handleFieldClick = (e, fieldId, hasValue) => {
    e.preventDefault();

    const element = document.getElementById(fieldId);
    if (element) {
      scroller.scrollTo(fieldId, {
        duration: 100,
        offset: -300,
        smooth: "easeInOutQuint",
        containerId: "analysis-report",
      });

      setTimeout(() => {
        const highlightClass = hasValue ? "highlight-non-missing" : "highlight-error";
        element.classList.add(highlightClass);

        setTimeout(() => {
          element.classList.remove(highlightClass);
        }, 5000);
      }, 300);
    }
  };

  // Get Taric JSON from Redux to determine valid fields
  const taricJson = useSelector(state => state.preparedJson.taricJson);
  
  // Extract all valid field names from Taric JSON structure
  const getValidTaricFields = () => {
    const validFields = new Set();
    
    if (!taricJson || !taricJson.taric || !taricJson.taric.duas || !taricJson.taric.duas.exportaciones) {
      return validFields;
    }
    
    taricJson.taric.duas.exportaciones.forEach(declaration => {
      // Declaration-level fields
      Object.keys(declaration).forEach(key => {
        if (key !== 'partidas') {
          validFields.add(key);
        }
      });
      
      // Item-level fields
      if (declaration.partidas && Array.isArray(declaration.partidas)) {
        declaration.partidas.forEach(item => {
          Object.keys(item).forEach(key => {
            if (key !== 'documentos' && key !== 'embalajes') {
              validFields.add(key);
            }
          });
          
          // Document fields
          if (item.documentos && Array.isArray(item.documentos)) {
            item.documentos.forEach(doc => {
              Object.keys(doc).forEach(key => {
                if (!key.endsWith('_confidence')) {
                  validFields.add(`documents_${key}`);
                }
              });
            });
          }
          
          // Package fields
          if (item.embalajes && Array.isArray(item.embalajes)) {
            item.embalajes.forEach(embalaje => {
              Object.keys(embalaje).forEach(key => {
                if (key === 'marcas') {
                  validFields.add('packages_marcas_marca');
                } else if (!key.endsWith('_confidence')) {
                  validFields.add(`packages_${key}`);
                }
              });
            });
          }
        });
      }
    });
    
    return validFields;
  };
  
  const validTaricFields = getValidTaricFields();
  
  // Check if a field exists in Taric JSON structure
  const isFieldInTaricJson = (fieldName) => {
    // If no Taric JSON yet, don't filter (show all fields without inputs)
    if (!taricJson || validTaricFields.size === 0) {
      return true; // Show all fields if Taric JSON not available yet
    }
    
    // Extract base field name
    let baseFieldName = fieldName;
    
    if (fieldName.startsWith('documents_')) {
      // Check both with and without documents_ prefix
      const fieldWithoutPrefix = fieldName.replace('documents_', '');
      return validTaricFields.has(fieldName) || validTaricFields.has(fieldWithoutPrefix);
    } else if (fieldName.startsWith('packages_')) {
      if (fieldName.startsWith('packages_marcas_marca')) {
        return validTaricFields.has('packages_marcas_marca');
      }
      // Check both with and without packages_ prefix
      const fieldWithoutPrefix = fieldName.replace('packages_', '');
      return validTaricFields.has(fieldName) || validTaricFields.has(fieldWithoutPrefix);
    } else if (fieldName.includes('.')) {
      // Extract field name from partidas[0].fieldName format
      baseFieldName = fieldName.split('.').pop();
    } else if (fieldName.includes('[')) {
      // Extract field name from partidas[0] format
      baseFieldName = fieldName.split('[')[0];
    }
    
    return validTaricFields.has(baseFieldName);
  };
  
  // Check if there are any errors
  const hasErrors = (validationErrors && validationErrors.length > 0) || taricJsonErrors;

  return (
    <div className={`min-h-screen max-h-screen overflow-y-auto p-4 pt-10 pl-0 flex gap-4 text-left flex-col transition-all duration-200 ${isFieldOverviewCollapsed ? 'w-20' : 'w-72'}`}>
      <div className="ml-2 min-h-[40px] flex gap-0 items-center overflow-x-auto overflow-y-hidden rounded-full">
       <div 
           className="bg-white sticky z-10 left-0 p-1.5 rounded-full cursor-pointer collapse-container flex-shrink-0"
           onClick={() => {
             if (setIsFieldOverviewCollapsed) {
               setIsFieldOverviewCollapsed(!isFieldOverviewCollapsed);
             }
           }}
           title={isFieldOverviewCollapsed ? (isSpanish ? 'Expandir' : 'Expand') : (isSpanish ? 'Colapsar' : 'Collapse')}
         >
           <img 
             src={collapseIcon} 
             alt="collapse"
             className={`logo-icon h-7 ${!isFieldOverviewCollapsed ? 'scale-x-[-1]' : ''}`}
           />
         </div>
        {!isFieldOverviewCollapsed && (
          <>
            <button 
          onClick={() => setActiveTab('fieldOverview')}
          className={`h-10 rounded-full text cursor-pointer hover:opacity-75 transition-opacity py-1 rounded whitespace-nowrap flex-shrink-0 ${
            activeTab === 'fieldOverview' ? 'bg-gray-100 font-bold px-4' : 'pr-2'
          }`}
        >
          {getMainTranslation('fieldOverview', isSpanish)}
        </button>
        <button 
          onClick={() => setActiveTab('errors')}
          className={`h-10 rounded-full text cursor-pointer hover:opacity-75 transition-opacity py-1 rounded whitespace-nowrap flex-shrink-0 ${
            activeTab === 'errors' ? 'bg-gray-100 font-bold px-4' : 'px-2'
          }`}
        >
          {getMainTranslation('errors', isSpanish)}
        </button>
          </>
        )}
        
      </div>
      
      {!isFieldOverviewCollapsed && activeTab === 'errors' && (
        <>
          {validationErrors && (
            <div className={`flex-0 ${!taricJsonErrors  && "!flex-1"}`}>
              <ValidationErrors validationErrors={validationErrors} hasErrors={hasErrors} isSpanish={isSpanish} splitView={splitView}/>
            </div>
          )}
          {taricJsonErrors && (
            <div className={`flex-1 ${!validationErrors && validationErrors.length >! 0  && "flex-1"}`}>
              <TaricJsonErrors taricJsonErrors={taricJsonErrors} location={location}/>
            </div>
          )}
        </>
      )}
      
      {!isFieldOverviewCollapsed && activeTab === 'fieldOverview' && (
        <div className="flex-1 flex flex-col gap-1">
          {/* Subtabs */}
          <div className="flex gap-0 items-center overflow-x-auto pb-4 ml-3">
            <button 
              onClick={() => setActiveSubTab('allFields')}
              className={`h-8 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                activeSubTab === 'allFields' ? 'bg-gray-100 font-semibold px-3' : 'pr-3'
              }`}
            >
              {getMainTranslation('allFields', isSpanish)}
            </button>
            <button 
              onClick={() => setActiveSubTab('missingFields')}
              className={`h-8 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity px-3 py-1 whitespace-nowrap flex-shrink-0 ${
                activeSubTab === 'missingFields' ? 'bg-gray-200 font-semibold' : ''
              }`}
            >
              {getMainTranslation('missingFields', isSpanish)}
            </button>
            <button 
              onClick={() => setActiveSubTab('nonMissingFields')}
              className={`h-8 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity px-3 py-1 whitespace-nowrap flex-shrink-0 ${
                activeSubTab === 'nonMissingFields' ? 'bg-gray-200 font-semibold' : ''
              }`}
            >
              {getMainTranslation('nonMissingFields', isSpanish)}
            </button>
            <button 
              onClick={() => setActiveSubTab('fieldsWithoutInputs')}
              className={`h-8 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity px-3 py-1 whitespace-nowrap flex-shrink-0 ${
                activeSubTab === 'fieldsWithoutInputs' ? 'bg-gray-200 font-semibold' : ''
              }`}
            >
              {getMainTranslation('fieldsWithoutInputs', isSpanish)}
            </button>
            <button 
              onClick={() => setActiveSubTab('confidenceFields')}
              className={`h-8 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity px-3 py-1 whitespace-nowrap flex-shrink-0 ${
                activeSubTab === 'confidenceFields' ? 'bg-gray-200 font-semibold' : ''
              }`}
            >
              {getMainTranslation('confidenceFields', isSpanish)}
            </button>
          </div>
          
          {/* Required/Non-Required Filter Sub-tabs - Hide for confidence fields */}
          {activeSubTab !== 'confidenceFields' && (
            <div className="flex gap-0 items-center overflow-x-auto pb-2 ml-3">
              <button 
                onClick={() => setRequiredFilter('all')}
                className={`h-7 rounded-full text-xs cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                  requiredFilter === 'all' ? 'bg-gray-100 font-semibold px-3' : 'pr-3 text-gray-600'
                }`}
              >
                {isSpanish ? 'Todos' : 'All'}
              </button>
              <button 
                onClick={() => setRequiredFilter('required')}
                className={`h-7 rounded-full text-xs cursor-pointer hover:opacity-75 transition-opacity px-3 py-1 whitespace-nowrap flex-shrink-0 ${
                  requiredFilter === 'required' ? 'bg-red-100 font-semibold text-red-700' : 'text-gray-600'
                }`}
              >
                {isSpanish ? 'Requeridos' : 'Required'}
              </button>
              <button 
                onClick={() => setRequiredFilter('nonRequired')}
                className={`h-7 rounded-full text-xs cursor-pointer hover:opacity-75 transition-opacity px-3 py-1 whitespace-nowrap flex-shrink-0 ${
                  requiredFilter === 'nonRequired' ? 'bg-blue-100 font-semibold text-blue-700' : 'text-gray-600'
                }`}
              >
                {isSpanish ? 'No Requeridos' : 'Not Required'}
              </button>
            </div>
          )}
          
          {/* Field List Content */}
          <div className="flex-1 overflow-y-auto">
            {(() => {
              if (!extractedData || (Array.isArray(extractedData) && extractedData.length === 0)) {
                return <p className="text-gray-500 text-sm">{isSpanish ? 'No hay datos disponibles' : 'No data available'}</p>;
              }
              
              const declarations = Array.isArray(extractedData) ? extractedData : [extractedData];
              
              // Get validation error field IDs once
              const validationErrorFieldIds = getValidationErrorFieldIds();
              
              // Helper function to parse field ID and extract field info
              const parseFieldId = (fieldId) => {
                // IMPORTANT: Check item fields FIRST (more specific pattern) before declaration fields
                // Item field: fieldName_declarationIndex+1_itemIndex
                // Match everything up to the last two underscore-separated numbers
                const itemMatch = fieldId.match(/^(.+)_(\d+)_(\d+)$/);
                if (itemMatch && !fieldId.startsWith('documents_') && !fieldId.startsWith('packages_')) {
                  const [, fieldName, declNum, itemNum] = itemMatch;
                  return {
                    type: 'item',
                    fieldName,
                    declarationIndex: parseInt(declNum) - 1,
                    itemIndex: parseInt(itemNum),
                    docIndex: undefined,
                    embajaIndex: undefined,
                    marcaIndex: undefined
                  };
                }
                
                // Declaration field: fieldName_declarationIndex+1
                // Match everything up to the last underscore followed by a number
                // This must come AFTER item field check to avoid false matches
                const declMatch = fieldId.match(/^(.+)_(\d+)$/);
                if (declMatch && !fieldId.startsWith('documents_') && !fieldId.startsWith('packages_')) {
                  const [, fieldName, declNum] = declMatch;
                  return {
                    type: 'declaration',
                    fieldName,
                    declarationIndex: parseInt(declNum) - 1,
                    itemIndex: undefined,
                    docIndex: undefined,
                    embajaIndex: undefined,
                    marcaIndex: undefined
                  };
                }
                
                // Document field: documents_fieldName_declarationIndex+1_itemIndex_docIndex
                // Match documents_ prefix, then field name (may have underscores), then three numbers
                const docMatch = fieldId.match(/^documents_(.+)_(\d+)_(\d+)_(\d+)$/);
                if (docMatch) {
                  const [, fieldName, declNum, itemNum, docNum] = docMatch;
                  return {
                    type: 'document',
                    fieldName,
                    declarationIndex: parseInt(declNum) - 1,
                    itemIndex: parseInt(itemNum),
                    docIndex: parseInt(docNum),
                    embajaIndex: undefined,
                    marcaIndex: undefined
                  };
                }
                
                // Package marca field: packages_marcas_marca_declarationIndex+1_itemIndex_embajaIndex_marcaIndex
                const marcaMatch = fieldId.match(/^packages_marcas_marca_(\d+)_(\d+)_(\d+)_(\d+)$/);
                if (marcaMatch) {
                  const [, declNum, itemNum, embajaNum, marcaNum] = marcaMatch;
                  return {
                    type: 'package_marca',
                    fieldName: 'marcas_marca',
                    declarationIndex: parseInt(declNum) - 1,
                    itemIndex: parseInt(itemNum),
                    docIndex: undefined,
                    embajaIndex: parseInt(embajaNum),
                    marcaIndex: parseInt(marcaNum)
                  };
                }
                
                // Package field: packages_fieldName_declarationIndex+1_itemIndex_embajaIndex
                // Match packages_ prefix, then field name (may have underscores), then three numbers
                const pkgMatch = fieldId.match(/^packages_(.+)_(\d+)_(\d+)_(\d+)$/);
                if (pkgMatch) {
                  const [, fieldName, declNum, itemNum, embajaNum] = pkgMatch;
                  return {
                    type: 'package',
                    fieldName,
                    declarationIndex: parseInt(declNum) - 1,
                    itemIndex: parseInt(itemNum),
                    docIndex: undefined,
                    embajaIndex: parseInt(embajaNum),
                    marcaIndex: undefined
                  };
                }
                
                return null;
              };
              
              // Organize fields by declaration, items, documents, and packages
              const organizedFields = declarations.map((declaration, declIndex) => {
                const declarationFields = [];
                const itemFields = [];
                
                // Get declaration-level fields
                Object.keys(declaration).forEach(key => {
                  if (key === 'partidas' || key === 'documentos' || key === 'embalajes') return;
                  
                  const value = declaration[key];
                  const fieldId = getFieldId(key, declIndex, undefined);
                  const hasInput = hasInputElement(fieldId);
                  const isInValidationErrors = isFieldInValidationErrors(fieldId, validationErrorFieldIds);
                  // Field has value if it's not null, undefined, or empty string
                  const hasValue = value !== null && value !== undefined && value !== '';
                  
                  declarationFields.push({
                    declarationIndex: declIndex,
                    field: key,
                    value: value,
                    hasValue: hasValue,
                    hasInput: hasInput,
                    isInValidationErrors: isInValidationErrors
                  });
                });
                
                // Get item-level fields
                if (declaration.partidas && Array.isArray(declaration.partidas)) {
                  declaration.partidas.forEach((item, itemIndex) => {
                    const itemFieldList = [];
                    const documentFields = [];
                    const packageFields = [];
                    
                    // Item fields
                    Object.keys(item).forEach(key => {
                      if (key === 'documentos' || key === 'embalajes') return;
                      
                      const value = item[key];
                      const fieldId = getFieldId(`partidas[${itemIndex}].${key}`, declIndex, itemIndex);
                      const hasInput = hasInputElement(fieldId);
                      const isInValidationErrors = isFieldInValidationErrors(fieldId, validationErrorFieldIds);
                      // Field has value if it's not null, undefined, or empty string
                      const hasValue = value !== null && value !== undefined && value !== '';
                      
                      itemFieldList.push({
                        declarationIndex: declIndex,
                        itemIndex: itemIndex,
                        field: `partidas[${itemIndex}].${key}`,
                        value: value,
                        hasValue: hasValue,
                        hasInput: hasInput,
                        isInValidationErrors: isInValidationErrors
                      });
                    });
                    
                    // Document fields
                    if (item.documentos && Array.isArray(item.documentos)) {
                      item.documentos.forEach((doc, docIndex) => {
                        const docFieldList = [];
                        Object.keys(doc).forEach(key => {
                          const value = doc[key];
                          const fieldId = getFieldId(`documents_${key}`, declIndex, itemIndex, docIndex);
                          const hasInput = hasInputElement(fieldId);
                          const isInValidationErrors = isFieldInValidationErrors(fieldId, validationErrorFieldIds);
                          // Field has value if it's not null, undefined, or empty string
                          const hasValue = value !== null && value !== undefined && value !== '';
                          
                          docFieldList.push({
                            declarationIndex: declIndex,
                            itemIndex: itemIndex,
                            docIndex: docIndex,
                            field: `documents_${key}`,
                            value: value,
                            hasValue: hasValue,
                            hasInput: hasInput,
                            isInValidationErrors: isInValidationErrors
                          });
                        });
                        documentFields.push({
                          docIndex: docIndex,
                          fields: docFieldList
                        });
                      });
                    }
                    
                    // Package fields
                    if (item.embalajes && Array.isArray(item.embalajes)) {
                      item.embalajes.forEach((embalaje, embajaIndex) => {
                        const pkgFieldList = [];
                        Object.keys(embalaje).forEach(key => {
                          if (key === 'marcas') {
                            if (embalaje.marcas && Array.isArray(embalaje.marcas)) {
                              embalaje.marcas.forEach((marca, marcaIndex) => {
                                const value = marca.marca;
                                const fieldId = getFieldId('packages_marcas_marca', declIndex, itemIndex, undefined, embajaIndex, marcaIndex);
                                const hasInput = hasInputElement(fieldId);
                                const isInValidationErrors = isFieldInValidationErrors(fieldId, validationErrorFieldIds);
                                // Field is missing if it has no value OR if it's in validationErrors
                                // Field has value if it's not null, undefined, or empty string
                                const hasValue = value !== null && value !== undefined && value !== '';
                                
                                pkgFieldList.push({
                                  declarationIndex: declIndex,
                                  itemIndex: itemIndex,
                                  embajaIndex: embajaIndex,
                                  marcaIndex: marcaIndex,
                                  field: `packages_marcas_marca`,
                                  value: value,
                                  hasValue: hasValue,
                                  hasInput: hasInput,
                                  isInValidationErrors: isInValidationErrors
                                });
                              });
                            }
                          } else {
                            const value = embalaje[key];
                            const fieldId = getFieldId(`packages_${key}`, declIndex, itemIndex, undefined, embajaIndex);
                            const hasInput = hasInputElement(fieldId);
                            const isInValidationErrors = isFieldInValidationErrors(fieldId, validationErrorFieldIds);
                            // Field has value if it's not null, undefined, or empty string
                            const hasValue = value !== null && value !== undefined && value !== '';
                            
                            pkgFieldList.push({
                              declarationIndex: declIndex,
                              itemIndex: itemIndex,
                              embajaIndex: embajaIndex,
                              field: `packages_${key}`,
                              value: value,
                              hasValue: hasValue,
                              hasInput: hasInput,
                              isInValidationErrors: isInValidationErrors
                            });
                          }
                        });
                        packageFields.push({
                          embajaIndex: embajaIndex,
                          fields: pkgFieldList
                        });
                      });
                    }
                    
                    itemFields.push({
                      itemIndex: itemIndex,
                      fields: itemFieldList,
                      documentFields: documentFields,
                      packageFields: packageFields
                    });
                  });
                }
                
                return {
                  declarationIndex: declIndex,
                  declarationFields: declarationFields,
                  itemFields: itemFields
                };
              });
              
              // Add missing fields from validationErrors that don't exist in the data
              // Collect all field IDs and their error labels from validationErrors
              const allValidationErrorFields = []; // Array of {fieldId, errorLabel}
              if (validationErrors && Array.isArray(validationErrors)) {
                validationErrors.forEach(declaration => {
                  // Declaration errors
                  if (declaration.declarationErrors) {
                    declaration.declarationErrors.forEach(error => {
                      if (error.field) {
                        allValidationErrorFields.push({ fieldId: error.field, errorLabel: error.error });
                      }
                    });
                  }
                  
                  // Item errors
                  if (declaration.itemErrors) {
                    declaration.itemErrors.forEach(item => {
                      if (item.errors) {
                        item.errors.forEach(error => {
                          if (error.field) {
                            allValidationErrorFields.push({ fieldId: error.field, errorLabel: error.error });
                          }
                        });
                      }
                    });
                  }
                  
                  // Document errors
                  if (declaration.documentErrors) {
                    declaration.documentErrors.forEach(doc => {
                      if (doc.errors) {
                        doc.errors.forEach(error => {
                          if (error.field) {
                            allValidationErrorFields.push({ fieldId: error.field, errorLabel: error.error });
                          }
                        });
                      }
                    });
                  }
                  
                  // Package errors
                  if (declaration.packageErrors) {
                    declaration.packageErrors.forEach(pkg => {
                      if (pkg.errors) {
                        pkg.errors.forEach(error => {
                          if (error.field) {
                            allValidationErrorFields.push({ fieldId: error.field, errorLabel: error.error });
                          }
                        });
                      }
                    });
                  }
                });
              }
              
              // Process each validation error field ID
              allValidationErrorFields.forEach(({ fieldId, errorLabel }) => {
                // Try both normalized and original field ID
                const normalizedId = normalizeValidationErrorFieldId(fieldId);
                const fieldInfo = parseFieldId(normalizedId) || parseFieldId(fieldId);
                if (!fieldInfo) return;
                
                const { type, fieldName, declarationIndex, itemIndex, docIndex, embajaIndex, marcaIndex } = fieldInfo;
                
                // Skip if declaration index is out of bounds
                if (declarationIndex < 0 || declarationIndex >= organizedFields.length) return;
                
                const decl = organizedFields[declarationIndex];
                
                if (type === 'declaration') {
                  // Check if field already exists
                  const exists = decl.declarationFields.some(f => f.field === fieldName);
                  if (!exists) {
                    const generatedFieldId = getFieldId(fieldName, declarationIndex, undefined);
                    const hasInput = hasInputElement(generatedFieldId);
                    const isInValidationErrors = true; // This field is from validationErrors
                    
                    decl.declarationFields.push({
                      declarationIndex: declarationIndex,
                      field: fieldName,
                      value: null,
                      hasValue: false,
                      hasInput: hasInput,
                      isInValidationErrors: isInValidationErrors
                    });
                  }
                } else if (type === 'item') {
                  // Find or create item - use find to avoid sparse array issues
                  let item = decl.itemFields.find(i => i && i.itemIndex === itemIndex);
                  if (!item) {
                    item = {
                      itemIndex: itemIndex,
                      fields: [],
                      documentFields: [],
                      packageFields: []
                    };
                    decl.itemFields.push(item);
                  }
                  
                  // Check if field already exists
                  const exists = item.fields.some(f => f.field === `partidas[${itemIndex}].${fieldName}`);
                  if (!exists) {
                    const generatedFieldId = getFieldId(`partidas[${itemIndex}].${fieldName}`, declarationIndex, itemIndex);
                    const hasInput = hasInputElement(generatedFieldId);
                    const isInValidationErrors = true;
                    
                    item.fields.push({
                      declarationIndex: declarationIndex,
                      itemIndex: itemIndex,
                      field: `partidas[${itemIndex}].${fieldName}`,
                      value: null,
                      hasValue: false,
                      hasInput: hasInput,
                      isInValidationErrors: isInValidationErrors
                    });
                  }
                } else if (type === 'document') {
                  // Find or create item - use find to avoid sparse array issues
                  let item = decl.itemFields.find(i => i && i.itemIndex === itemIndex);
                  if (!item) {
                    item = {
                      itemIndex: itemIndex,
                      fields: [],
                      documentFields: [],
                      packageFields: []
                    };
                    decl.itemFields.push(item);
                  }
                  // Ensure document exists
                  if (!item.documentFields[docIndex]) {
                    item.documentFields[docIndex] = {
                      docIndex: docIndex,
                      fields: []
                    };
                  }
                  
                  const doc = item.documentFields[docIndex];
                  // Check if field already exists
                  const exists = doc.fields.some(f => f.field === `documents_${fieldName}`);
                  if (!exists) {
                    const generatedFieldId = getFieldId(`documents_${fieldName}`, declarationIndex, itemIndex, docIndex);
                    const hasInput = hasInputElement(generatedFieldId);
                    const isInValidationErrors = true;
                    
                    doc.fields.push({
                      declarationIndex: declarationIndex,
                      itemIndex: itemIndex,
                      docIndex: docIndex,
                      field: `documents_${fieldName}`,
                      value: null,
                      hasValue: false,
                      hasInput: hasInput,
                      isInValidationErrors: isInValidationErrors
                    });
                  }
                } else if (type === 'package' || type === 'package_marca') {
                  // Find or create item - use find to avoid sparse array issues
                  let item = decl.itemFields.find(i => i && i.itemIndex === itemIndex);
                  if (!item) {
                    item = {
                      itemIndex: itemIndex,
                      fields: [],
                      documentFields: [],
                      packageFields: []
                    };
                    decl.itemFields.push(item);
                  }
                  // Ensure package exists
                  if (!item.packageFields[embajaIndex]) {
                    item.packageFields[embajaIndex] = {
                      embajaIndex: embajaIndex,
                      fields: []
                    };
                  }
                  
                  const pkg = item.packageFields[embajaIndex];
                  
                  if (type === 'package_marca') {
                    // Check if marca field already exists
                    const exists = pkg.fields.some(f => 
                      f.field === `packages_marcas_marca` && 
                      f.embajaIndex === embajaIndex && 
                      f.marcaIndex === marcaIndex
                    );
                    if (!exists) {
                      const generatedFieldId = getFieldId('packages_marcas_marca', declarationIndex, itemIndex, undefined, embajaIndex, marcaIndex);
                      const hasInput = hasInputElement(generatedFieldId);
                      const isInValidationErrors = true;
                      
                      pkg.fields.push({
                        declarationIndex: declarationIndex,
                        itemIndex: itemIndex,
                        embajaIndex: embajaIndex,
                        marcaIndex: marcaIndex,
                        field: `packages_marcas_marca`,
                        value: null,
                        hasValue: false,
                        hasInput: hasInput,
                        isInValidationErrors: isInValidationErrors
                      });
                    }
                  } else {
                    // Check if package field already exists
                    const exists = pkg.fields.some(f => f.field === `packages_${fieldName}`);
                    if (!exists) {
                      const generatedFieldId = getFieldId(`packages_${fieldName}`, declarationIndex, itemIndex, undefined, embajaIndex);
                      const hasInput = hasInputElement(generatedFieldId);
                      const isInValidationErrors = true;
                      
                      pkg.fields.push({
                        declarationIndex: declarationIndex,
                        itemIndex: itemIndex,
                        embajaIndex: embajaIndex,
                        field: `packages_${fieldName}`,
                        value: null,
                        hasValue: false,
                        hasInput: hasInput,
                        isInValidationErrors: isInValidationErrors
                      });
                    }
                  }
                }
              });
              
              // Sort itemFields by itemIndex to ensure proper ordering
              organizedFields.forEach(decl => {
                decl.itemFields = decl.itemFields
                  .filter(item => item !== undefined && item !== null)
                  .sort((a, b) => a.itemIndex - b.itemIndex);
              });
              
              // Helper to check if field is a confidence field
              const isConfidenceField = (fieldName) => {
                return fieldName.endsWith('_confidence');
              };
              
              // Filter based on active subtab and required filter
              const filterField = (field) => {
                // Extract field name to check if it's a confidence field
                let fieldName = field.field;
                if (field.field.includes('.')) {
                  fieldName = field.field.split('.').pop();
                } else if (field.field.includes('[')) {
                  fieldName = field.field.split('[')[0];
                }
                if (field.field.startsWith('documents_')) {
                  fieldName = field.field.replace('documents_', '');
                } else if (field.field.startsWith('packages_')) {
                  if (field.field.startsWith('packages_marcas_marca')) {
                    fieldName = 'marcas_marca';
                  } else {
                    fieldName = field.field.replace('packages_', '');
                  }
                }
                
                const isConfidence = isConfidenceField(fieldName);
                
                // Check required filter
                const fieldId = getFieldId(
                  field.field, 
                  field.declarationIndex, 
                  field.itemIndex, 
                  field.docIndex, 
                  field.embajaIndex, 
                  field.marcaIndex
                );
                const isRequired = isFieldInValidationErrors(fieldId, validationErrorFieldIds);
                
                // Apply required filter
                if (requiredFilter === 'required' && !isRequired) {
                  return false;
                }
                if (requiredFilter === 'nonRequired' && isRequired) {
                  return false;
                }
                
                if (activeSubTab === 'missingFields') {
                  return !field.hasValue && field.hasInput && !isConfidence;
                } else if (activeSubTab === 'nonMissingFields') {
                  return field.hasValue && field.hasInput && !isConfidence;
                } else if (activeSubTab === 'fieldsWithoutInputs') {
                  // Only show fields without inputs that exist in Taric JSON structure
                  // Exclude version field
                  const isVersion = fieldName === 'version' || field.field === 'version';
                  return !field.hasInput && !isConfidence && !isVersion && isFieldInTaricJson(field.field);
                } else if (activeSubTab === 'confidenceFields') {
                  return isConfidence;
                } else if (activeSubTab === 'allFields') {
                  // Exclude confidence fields and version field from "All Fields" tab
                  const isVersion = fieldName === 'version' || field.field === 'version';
                  return !isConfidence && !isVersion;
                }
                return true;
              };
              
              // Check if there are any fields to display
              const hasFields = organizedFields.some(decl => {
                const hasDeclFields = decl.declarationFields.some(filterField);
                const hasItemFields = decl.itemFields.some(item => {
                  const hasItemF = item.fields.some(filterField);
                  const hasDocFields = item.documentFields.some(doc => doc.fields.some(filterField));
                  const hasPkgFields = item.packageFields.some(pkg => pkg.fields.some(filterField));
                  return hasItemF || hasDocFields || hasPkgFields;
                });
                return hasDeclFields || hasItemFields;
              });
              
              if (!hasFields) {
                return (
                  <p className="text-gray-500 text-sm">
                    {isSpanish 
                      ? activeSubTab === 'missingFields' 
                        ? 'No hay campos faltantes' 
                        : activeSubTab === 'nonMissingFields'
                        ? 'No hay campos completados'
                        : activeSubTab === 'fieldsWithoutInputs'
                        ? 'No hay campos sin entradas'
                        : activeSubTab === 'confidenceFields'
                        ? 'No hay campos de confianza'
                        : 'No hay campos disponibles'
                      : activeSubTab === 'missingFields'
                        ? 'No missing fields'
                        : activeSubTab === 'nonMissingFields'
                        ? 'No non-missing fields'
                        : activeSubTab === 'fieldsWithoutInputs'
                        ? 'No fields without inputs'
                        : activeSubTab === 'confidenceFields'
                        ? 'No confidence fields'
                        : 'No fields available'
                    }
                  </p>
                );
              }
              
              // Render field card component
              const renderFieldCard = (field, index) => {
                // Extract the actual field name for display
                let actualFieldName = field.field;
                if (field.field.startsWith('documents_')) {
                  actualFieldName = field.field.replace('documents_', '');
                } else if (field.field.startsWith('packages_')) {
                  if (field.field.startsWith('packages_marcas_marca')) {
                    actualFieldName = 'marcas_marca';
                  } else {
                    actualFieldName = field.field.replace('packages_', '');
                  }
                } else if (field.field.includes('.')) {
                  actualFieldName = field.field.split('.').pop();
                } else if (field.field.includes('[') && field.field.includes(']')) {
                  actualFieldName = field.field.split('[')[0];
                }
                
                const isConfidence = isConfidenceField(actualFieldName);
                const fieldId = getFieldId(
                  field.field, 
                  field.declarationIndex, 
                  field.itemIndex, 
                  field.docIndex, 
                  field.embajaIndex, 
                  field.marcaIndex
                );
                const hasInput = field.hasInput !== undefined ? field.hasInput : hasInputElement(fieldId);
                
                // Check if field is required (always check against validationErrorFieldIds, not just when missing)
                const isRequired = isFieldInValidationErrors(fieldId, validationErrorFieldIds);
                
                // Get field label - prefer label from validationErrors to match ValidationErrors.jsx
                const validationErrorLabel = getFieldLabelFromValidationErrors(fieldId);
                const fieldLabel = validationErrorLabel || getFieldDisplayName(field.field);
                
                // Determine background color for "All Fields" tab
                let bgColor = '';
                if (isConfidence) {
                  bgColor = 'bg-blue-50';
                } else if (!field.hasInput) {
                  bgColor = 'bg-gray-100';
                  } else if (!field.hasValue) {
                  bgColor = 'bg-red-50';
                  } else {
                  bgColor = 'bg-secondary';
                  }
                
                return (
                  <div 
                    key={`${field.declarationIndex}-${field.field}-${index}`}
                    className={`p-3 px-5 rounded-full text-sm break-words ${bgColor} ${hasInput ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''} relative`}
                    onClick={hasInput ? (e) => handleFieldClick(e, fieldId, field.hasValue) : undefined}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 flex gap-1 truncate rounded-full">
                        <span className={`font-semibold text-xs break-words ${hasInput ? 'text-gray-700' : 'text-gray-500'}`}>
                          {fieldLabel}
                        </span>
                        <div className="text-[8px] text-gray-500 font-mono break-all truncate bottom-[-0px] relative">
                          {actualFieldName}
                        </div>
                      </div>
                      {isRequired && requiredFilter === 'all' && (
                        <span className="text-[10px] text-red-600 font-semibold whitespace-nowrap flex-shrink-0">
                          {isSpanish ? 'Requerido' : 'Required'}
                        </span>
                      )}
                    </div>
                    {isConfidence && (
                      <div className="text-xs break-words text-blue-600 font-semibold">
                        {field.hasValue 
                          ? `${field.value}%`
                          : (isSpanish ? 'Sin valor' : 'No value')
                        }
                      </div>
                    )}
                    {!isConfidence && !(activeSubTab === 'missingFields' && !field.hasValue) && (
                      <div className={`truncate text-xs break-words ${field.hasValue ? 'text-gray-600' : 'text-red-500'}`}>
                        {field.hasValue 
                          ? (typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value))
                          : (isSpanish ? 'Sin valor' : 'No value')
                        }
                      </div>
                    )}
                  </div>
                );
              };
              
              return (
                <div className="space-y-4">
                  {organizedFields.map((decl, declIndex) => {
                    const filteredDeclFields = decl.declarationFields.filter(filterField);
                    const hasAnyFields = filteredDeclFields.length > 0 || 
                      decl.itemFields.some(item => {
                        const hasItemF = item.fields.filter(filterField).length > 0;
                        const hasDocFields = item.documentFields.some(doc => doc.fields.filter(filterField).length > 0);
                        const hasPkgFields = item.packageFields.some(pkg => pkg.fields.filter(filterField).length > 0);
                        return hasItemF || hasDocFields || hasPkgFields;
                      });
                    
                    if (!hasAnyFields) return null;
                    
                    return (
                      <div key={declIndex} className="mb-4">
                        <h3 className="font-semibold text-base mb-2 text-gray-700 ml-5">
                          {!isSpanish ? "Export" : "Expediente"} #{decl.declarationIndex + 1}
                        </h3>
                        
                        {/* Declaration Fields */}
                        {filteredDeclFields.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {filteredDeclFields.map((field, index) => renderFieldCard(field, index))}
                          </div>
                        )}
                        
                        {/* Item Fields */}
                        {decl.itemFields.filter(item => item !== undefined && item !== null).map((item, itemIdx) => {
                          const filteredItemFields = item.fields.filter(filterField);
                          const filteredDocFields = item.documentFields.map(doc => ({
                            ...doc,
                            fields: doc.fields.filter(filterField)
                          })).filter(doc => doc.fields.length > 0);
                          const filteredPkgFields = item.packageFields.map(pkg => ({
                            ...pkg,
                            fields: pkg.fields.filter(filterField)
                          })).filter(pkg => pkg.fields.length > 0);
                          
                          const hasItemData = filteredItemFields.length > 0 || 
                            filteredDocFields.length > 0 || 
                            filteredPkgFields.length > 0;
                          
                          if (!hasItemData) return null;
                          
                          return (
                            <div key={itemIdx} className="ml-4 mb-3">
                              <h4 className="font-medium text-sm mb-2 text-gray-600 ml-5">
                                {!isSpanish ? "Item" : "Partida"} #{item.itemIndex + 1}
                              </h4>
                              
                              {/* Item Fields */}
                              {filteredItemFields.length > 0 && (
                                <div className="space-y-2 mb-2">
                                  {filteredItemFields.map((field, index) => renderFieldCard(field, index))}
                                </div>
                              )}
                              
                              {/* Document Fields */}
                              {filteredDocFields.length > 0 && (
                                <div className="ml-4 mb-2">
                                  <h5 className="font-medium text-sm mb-1 text-gray-500 ml-9">
                                    {!isSpanish ? "Documents" : "Documentos"}
                                  </h5>
                                  {filteredDocFields.map((doc, docIdx) => (
                                    <div key={docIdx} className="ml-4 mb-2">
                                      <p className="text-xs font-medium mb-1 text-gray-500 ml-5">
                                        {!isSpanish ? "Document" : "Documento"} #{doc.docIndex + 1}
                                      </p>
                                      <div className="space-y-2">
                                        {doc.fields.map((field, index) => renderFieldCard(field, index))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Package Fields */}
                              {filteredPkgFields.length > 0 && (
                                <div className="ml-4 mb-2">
                                  <h5 className="font-medium text-sm mb-1 text-gray-500 ml-9">
                                    {!isSpanish ? "Packages" : "Embalajes"}
                                  </h5>
                                  {filteredPkgFields.map((pkg, pkgIdx) => (
                                    <div key={pkgIdx} className="ml-4 mb-2">
                                      <p className="text-xs font-medium mb-1 text-gray-500 ml-5">
                                        {!isSpanish ? "Package" : "Embalaje"} #{pkg.embajaIndex + 1}
                                      </p>
                                      <div className="space-y-2">
                                        {pkg.fields.map((field, index) => renderFieldCard(field, index))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

